import React, { useState } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, TablePagination, useTheme
} from '@mui/material';
import { AttachMoney as Icon, TrendingUp, TrendingDown } from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useQuery } from 'react-query';

const fetchStats = async () => {
  const res = await fetch('/api/admin/stats', {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });
  return res.json();
};

const fetchPagos = async () => {
  const res = await fetch('/api/pagos', {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });
  return res.json();
};

export default function Finanzas() {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data: statsData } = useQuery('stats', fetchStats, { refetchInterval: 30000 });
  const { data: pagosData } = useQuery('pagos', fetchPagos, { refetchInterval: 30000 });

  const stats = statsData?.data || {};
  const pagos = pagosData?.data || [];

  const totalIngresos = pagos.filter(p => p.estado === 'completado').reduce((s, p) => s + parseFloat(p.monto || 0), 0);
  const pendientes = pagos.filter(p => p.estado === 'pendiente');
  const completados = pagos.filter(p => p.estado === 'completado').length;
  const fallidos = pagos.filter(p => p.estado === 'fallido').length;

  const pageData = pagos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const resumenDiario = [
    { label: 'Ingresos Hoy', value: `$${(stats.ingresos_hoy || 0).toFixed(2)}`, icon: <TrendingUp />, color: 'success.main' },
    { label: 'Pedidos Hoy', value: stats.pedidos_hoy || 0, icon: null, color: 'primary.main' },
    { label: 'Total Ingresos', value: `$${totalIngresos.toFixed(2)}`, icon: null, color: 'secondary.main' },
    { label: 'Transacciones', value: pagos.length, icon: null, color: 'text.primary' },
  ];

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Icon sx={{ color: theme.palette.primary.main, fontSize: 32 }} />
        <Typography variant="h4" fontWeight={700}>Finanzas</Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {resumenDiario.map((item, i) => (
          <Grid item xs={6} md={3} key={i}>
            <Card><CardContent sx={{ py: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography color="textSecondary" variant="caption">{item.label}</Typography>
                  <Typography variant="h5" sx={{ color: item.color }}>{item.value}</Typography>
                </Box>
                {item.icon && <Box sx={{ color: item.color }}>{item.icon}</Box>}
              </Box>
            </CardContent></Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Ingresos Semanales</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={stats.grafico_data || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="dia" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="ingresos" stroke={theme.palette.primary.main} strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Resumen de Pagos</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Chip label="Completados" color="success" size="small" />
                <Typography variant="h6">{completados}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Chip label="Pendientes" color="warning" size="small" />
                <Typography variant="h6">{pendientes.length}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Chip label="Fallidos" color="error" size="small" />
                <Typography variant="h6">{fallidos}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: 1, borderColor: 'divider' }}>
                <Typography fontWeight={700}>Total cobrado</Typography>
                <Typography variant="h6" color="success.main">${totalIngresos.toFixed(2)}</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Historial de Transacciones</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Monto</strong></TableCell>
              <TableCell><strong>Método</strong></TableCell>
              <TableCell><strong>Estado</strong></TableCell>
              <TableCell><strong>Referencia</strong></TableCell>
              <TableCell><strong>Pedido</strong></TableCell>
              <TableCell><strong>Fecha</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageData.map(p => (
              <TableRow key={p.id} hover>
                <TableCell>#{p.id}</TableCell>
                <TableCell><strong>${parseFloat(p.monto || 0).toFixed(2)}</strong></TableCell>
                <TableCell>{p.metodo || '—'}</TableCell>
                <TableCell>
                  <Chip label={p.estado} size="small"
                    color={p.estado === 'completado' ? 'success' : p.estado === 'pendiente' ? 'warning' : 'error'} />
                </TableCell>
                <TableCell>{p.referencia || p.transaccion_id ? p.transaccion_id.substring(0, 12) + '...' : '—'}</TableCell>
                <TableCell>{p.Pedido ? `#${p.Pedido.id}` : '—'}</TableCell>
                <TableCell>{p.createdAt ? new Date(p.createdAt).toLocaleString('es') : '—'}</TableCell>
              </TableRow>
            ))}
            {pageData.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center"><Typography color="textSecondary">Sin transacciones</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div" count={pagos.length} page={page} onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage} rowsPerPageOptions={[10, 25, 50]}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </TableContainer>
    </Box>
  );
}
